'use client'

import { Button } from '@brew/ui/button'
import { Loader2, Sparkles } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useCapabilityGate } from '@/hooks/use-can'
import { getErrorMessage } from '@/lib/shared/errors/error-message'
import { appToast } from '@/lib/shared/toast'

export type InboundGenerateUrlButtonProps = {
  providerId: string
  webhookUrl?: string
  onGenerated: (args: { connectionId: string; webhookUrl: string }) => void
}

/**
 * Issues a per-brand inbound connectionId via
 * `POST /api/integrations/inbound/connection?provider=<id>` and
 * pre-fills the dialog's `settings.webhookUrl` so the catalog's
 * `'copy'` tutorial step displays the URL the user pastes into the
 * provider's dashboard.
 *
 * The endpoint is idempotent: re-clicking after a partial setup
 * surfaces the existing connectionId so the URL doesn't churn.
 */
export function InboundGenerateUrlButton({
  providerId,
  webhookUrl,
  onGenerated,
}: InboundGenerateUrlButtonProps) {
  const workGate = useCapabilityGate('integrations.manage')
  const [isGenerating, setIsGenerating] = useState(false)

  const handleClick = useCallback(async () => {
    setIsGenerating(true)
    try {
      const response = await fetch(
        `/api/integrations/inbound/connection?provider=${encodeURIComponent(providerId)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )
      const data = (await response.json()) as {
        connectionId?: string
        webhookUrl?: string
        error?: string
      }
      if (!response.ok || !data.connectionId || !data.webhookUrl) {
        throw new Error(data.error ?? 'Failed to generate webhook URL')
      }
      onGenerated({
        connectionId: data.connectionId,
        webhookUrl: data.webhookUrl,
      })
      appToast.success(
        'Webhook URL ready — copy it into your provider dashboard.'
      )
    } catch (error) {
      appToast.error(getErrorMessage(error, 'Failed to generate URL'))
    } finally {
      setIsGenerating(false)
    }
  }, [onGenerated, providerId])

  if (webhookUrl) {
    return null
  }

  return (
    <Button
      type="button"
      variant="outline"
      className="mt-2 w-full"
      onClick={handleClick}
      disabled={isGenerating || !workGate.isAllowed}
      title={workGate.reason ?? undefined}
    >
      {isGenerating ? (
        <Loader2 className="mr-2 size-4 animate-spin" />
      ) : (
        <Sparkles className="mr-2 size-4" />
      )}
      Generate webhook URL
    </Button>
  )
}
